<template>
  <el-dialog
    :custom-class="customClass"
    :visible.sync="dialogVisible"
    center
    :close-on-press-escape="false"
    :close-on-click-modal="false"
    :title="title"
    v-bind="$attrs"
    @close="handleClose"
  >
    <slot></slot>
  </el-dialog>
</template>

<script>
import { isH5, isMobile } from "@saas/components/src";
export default {
  props: {
    title: {
      type: String,
      default: "",
    },
    show: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "small",
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        this.dialogVisible = val;
      },
    },
  },
  computed: {
    customClass() {
      if (this.size === "small") {
        return this.isH5
          ? "saas-web-dialog__small mg-dialog__h5"
          : "saas-web-dialog__small";
      } else if (this.size === "middle") {
        return this.isH5
          ? "saas-web-dialog__middle mg-dialog__h5"
          : "saas-web-dialog__middle";
      } else {
        return this.isH5
          ? "saas-web-dialog__large mg-dialog__h5"
          : "saas-web-dialog__large";
      }
    },
  },
  data() {
    return {
      dialogVisible: false,
      isH5: isH5(),
    };
  },
  methods: {
    handleClose() {
      this.dialogVisible = false;
      this.$emit("cancel");
      this.$emit("update:show", false);
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .el-dialog {
  --mg-dialog-bg: #ffffff;
  --mg-dialog-title: #0d1400;
  --mg-dialog-label: #515350;
  --mg-dialog-close: #909399;
  --mg-dialog-input-bg: #ffffff;
  --mg-dialog-input-border: #dcdfe6;
  --mg-dialog-input-text: #0d1400;
  --mg-dialog-placeholder: #575859;
  --mg-dialog-default-bg: #f2f3f5;
  --mg-dialog-default-text: #4e5969;
  --mg-dialog-checkbox-text: #515350;

  margin: 0 auto !important;
  top: 50%;
  transform: translateY(-50%);
  background: var(--mg-dialog-bg);
  .el-dialog__header {
    text-align: center;
  }
  .el-dialog__title {
    font-weight: 500;
    color: var(--mg-dialog-title);
  }
  .el-dialog__close {
    font-weight: bold;
    color: var(--mg-dialog-close);
  }
  .el-button--primary {
    background: linear-gradient(132deg, #00eb81 0%, #00c89d 100%);
    height: 36px;
    min-width: 70px;
    border-radius: 6px;
    padding: 0 15px;
    border: none;
    font-weight: 500;
    font-size: 14px;
    color: #ffffff;
  }
  .el-button--primary:focus,
  .el-button--primary:hover {
    background: linear-gradient(132deg, #00eb81 0%, #00c89d 100%) !important;
    border: unset;
  }
  .el-button--default {
    background: var(--mg-dialog-default-bg);
    height: 36px;
    min-width: 70px;
    border-radius: 6px;
    padding: 0 15px;
    border: none;
    font-weight: 500;
    font-size: 14px;
    color: var(--mg-dialog-default-text);
  }
  .el-button--default:focus,
  .el-button--default:hover {
    background: var(--mg-dialog-default-bg);
    border: unset;
  }
  .el-dialog__body {
    .el-form {
      .el-form-item__label {
        color: var(--mg-dialog-label);
      }
      .el-form-item__content {
        .el-input__inner,
        .el-textarea__inner {
          background: var(--mg-dialog-input-bg);
          border-color: var(--mg-dialog-input-border);
          border-radius: 6px;
          font-size: 14px;
          color: var(--mg-dialog-input-text);
          &::placeholder {
            color: var(--mg-dialog-placeholder);
          }
        }
      }
    }
    .el-checkbox {
      .el-checkbox__label {
        padding-left: 0;
        font-weight: normal;
        color: var(--mg-dialog-checkbox-text);
      }
    }
  }
}
::v-deep .saas-web-dialog__small.el-dialog {
  width: 380px;
  border-radius: 18px;
  .el-dialog__header {
    padding-top: 24px;
    padding-bottom: 12px;
    .el-dialog__headerbtn {
      right: 10px;
      top: 6px;
    }
  }
  .el-dialog__title {
    font-size: 14px;
  }
  .el-dialog__body {
    padding: 0 24px 24px 24px;
  }
}
::v-deep .saas-web-dialog__middle.el-dialog {
  width: 480px;
  border-radius: 24px;
  .el-dialog__header {
    padding-top: 42px;
    padding-bottom: 12px;
    .el-dialog__headerbtn {
      right: 20px;
      top: 16;
    }
  }
  .el-dialog__title {
    font-size: 18px;
  }
  .el-dialog__body {
    padding: 0 48px 40px 48px;
  }
}
::v-deep .el-dialog.mg-dialog__h5 {
  width: 9.3333rem;
  max-height: 80%;
  overflow: auto;
  border-radius: 0.48rem;
  .el-dialog__header {
    padding-top: 0.64rem;
    padding-bottom: 0.32rem;
    .el-dialog__headerbtn {
      right: 0.2667rem;
      top: 0.16rem;
    }
  }
  .el-dialog__title {
    font-size: 0.3733rem;
  }
  .el-dialog__body {
    padding: 0 0.64rem 0.64rem 0.64rem;
  }
}
</style>

<style lang="scss">
html.dark {
  .saas-web-dialog__small,
  .saas-web-dialog__middle,
  .saas-web-dialog__large,
  .mg-dialog__h5 {
    --mg-dialog-bg: var(--mg-bg-elevated);
    --mg-dialog-title: var(--mg-text-primary);
    --mg-dialog-label: var(--mg-text-secondary);
    --mg-dialog-close: var(--mg-text-muted);
    --mg-dialog-input-bg: var(--mg-control-bg);
    --mg-dialog-input-border: var(--mg-border-subtle);
    --mg-dialog-input-text: var(--mg-text-primary);
    --mg-dialog-placeholder: var(--mg-text-muted);
    --mg-dialog-default-bg: var(--mg-disabled-bg);
    --mg-dialog-default-text: var(--mg-text-primary);
    --mg-dialog-checkbox-text: var(--mg-text-secondary);
  }
}
</style>
